"use client";

import * as Headless from "@headlessui/react";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { NavbarItem } from "@/components/ui/navbar/Navbar";
import { CutoutMaskContainer } from "@/components/CutoutMaskContainer";
import { AniChatWidget } from "@/components/chat/AniChatWidget";
import { useAni } from "../../../context/AniContext";
import { useAuth } from "@/context/AuthContext";
import { useModulePermissions } from "@/context/ModulePermissionsContext";
import { useInAppAgentProfile } from "@/hooks/useInAppAgentProfile";
import type { Theme } from "@/config/themes";
import GradientBlobContainer from "@/components/ui/container/GradientBlobContainer";

const MORPH_ANIMATION_DURATION = 50; // ms, SLOWED DOWN for observation
const ROUTE_TRANSITION_GRACE_PERIOD_MS = 300; // Delay before hiding cutout on route change
const ROUTES_TO_HIDE_CUTOUT = [
  "/ani",
  "/another-route",
  "/worker/create",
  "/pod/create",
  "/worker/*", // Wildcard pattern to match all worker routes
];

function MobileSidebar({
  open,
  close,
  closeIcon,
  children,
}: React.PropsWithChildren<{
  open: boolean;
  close: () => void;
  closeIcon: React.ReactNode;
}>) {
  return (
    <Headless.Dialog open={open} onClose={close} className="lg:hidden">
      <Headless.DialogBackdrop
        transition
        className="fixed inset-0 bg-black/30 transition data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in"
      />
      <Headless.DialogPanel
        transition
        className="fixed inset-y-0 w-full max-w-80 p-2 transition duration-300 ease-in-out data-closed:-translate-x-full"
      >
        <div className="flex h-full flex-col rounded-lg bg-slate-900 ring-1 shadow-xs ring-slate-950/5 dark:bg-slate-900 dark:ring-white/10">
          <div className="-mb-3 px-4 pt-3">
            <Headless.CloseButton as={NavbarItem} aria-label="Close navigation">
              {closeIcon}
            </Headless.CloseButton>
          </div>
          <div className="text-white font-sans">{children}</div>
        </div>
      </Headless.DialogPanel>
    </Headless.Dialog>
  );
}

export function SidebarLayout({
  navbar,
  sidebar,
  children,
  openIcon,
  closeIcon,
  hasColumns = false,
  companyId: _companyId,
  currentTheme,
  workspaceBanner,
}: React.PropsWithChildren<{
  navbar: React.ReactNode;
  sidebar: React.ReactNode;
  openIcon: React.ReactNode;
  closeIcon: React.ReactNode;
  hasColumns?: boolean;
  companyId?: string;
  currentTheme?: Theme;
  workspaceBanner?: React.ReactNode;
}>) {
  const { resourceId, chatContext, apiData, onApiAction, widgetViewState, hidden: aniHidden } =
    useAni();
  const { user } = useAuth();
  const { hasPermission } = useModulePermissions();
  const canUseAni = hasPermission("ani");
  useInAppAgentProfile(canUseAni ? user?.company_id : undefined);
  const [showSidebar, setShowSidebar] = useState(false);
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== "undefined"
      ? window.matchMedia("(min-width: 1024px)").matches
      : true
  );
  const location = useLocation();
  const pathname = location.pathname;

  const mainContentRef = useRef<HTMLDivElement>(null);
  const cutoutContainerRef = useRef<HTMLDivElement>(null);
  const [isAniManuallyHidden, setIsAniManuallyHidden] = useState(false);
  const [, setIsAniBeingDragged] = useState(false);
  const [cutoutForceOpenedByAni, setCutoutForceOpenedByAni] = useState(false);

  // --- Morph Animation State and Logic ---
  const [isMorphedToSimple] = useState(false);
  const [currentMorphProgress, setCurrentMorphProgress] = useState(0);
  const animationFrameId = useRef<number | null>(null);

  const targetMorphProgress = isMorphedToSimple ? 1 : 0;

  useEffect(() => {
    const startValue = currentMorphProgress;
    let startTime: number | null = null;

    // Easing function: ease-in-out quadratic
    const easeInOutQuad = (t: number): number => {
      return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
    };

    const animate = (timestamp: number) => {
      if (!startTime) {
        startTime = timestamp;
      }
      const elapsedTime = timestamp - startTime;
      const linearProgress = Math.min(
        elapsedTime / MORPH_ANIMATION_DURATION,
        1
      );

      const easedProgress = easeInOutQuad(linearProgress); // Apply easing

      const newValue =
        startValue + (targetMorphProgress - startValue) * easedProgress; // Use eased progress
      setCurrentMorphProgress(newValue);

      if (linearProgress < 1) {
        // Check linearProgress for completion
        animationFrameId.current = requestAnimationFrame(animate);
      } else {
        setCurrentMorphProgress(targetMorphProgress); // Ensure it lands exactly
      }
    };

    if (animationFrameId.current) {
      cancelAnimationFrame(animationFrameId.current);
    }

    if (currentMorphProgress !== targetMorphProgress) {
      animationFrameId.current = requestAnimationFrame(animate);
    }

    return () => {
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }
    };
  }, [targetMorphProgress, currentMorphProgress]);

  // --- End of Morph Animation State and Logic ---

  // --- Cutout Visibility Logic with Delay ---
  // Helper function to check if a pathname matches any route pattern (including wildcards)
  const matchesRoutePattern = (
    pathname: string,
    patterns: string[]
  ): boolean => {
    return patterns.some((pattern) => {
      if (pattern.includes("*")) {
        // Convert wildcard pattern to regex
        const regexPattern = pattern
          .replace(/[.*+?^${}()|[\]\\]/g, "\\$&") // First escape regex special characters
          .replace(/\\\*/g, ".*"); // Then replace escaped * with .*
        const regex = new RegExp(`^${regexPattern}$`);
        return regex.test(pathname);
      }
      return pattern === pathname;
    });
  };

  const [currentCutoutVisibleProp, setCurrentCutoutVisibleProp] =
    useState(true);
  const hideCutoutTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // This effect determines the "base" visibility according to routes/tabs
    // It also manages the cutoutForceOpenedByAni state in conjunction with route changes.

    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get("tab");
    const shouldHideBasedOnRoute = matchesRoutePattern(pathname, ROUTES_TO_HIDE_CUTOUT);
    const routeWantsToHide = shouldHideBasedOnRoute || tabParam === "channel";

    if (hideCutoutTimerRef.current) {
      clearTimeout(hideCutoutTimerRef.current);
      hideCutoutTimerRef.current = null;
    }

    if (cutoutForceOpenedByAni) {
      // If Ani is forcing it open, ensure currentCutoutVisibleProp is true.
      setCurrentCutoutVisibleProp(true);
      if (!routeWantsToHide) {
        // If route logic now says "open" anyway, Ani's force is no longer needed.
        setCutoutForceOpenedByAni(false);
      }
      return; // Ani's force state takes precedence for this rendering cycle.
    }

    // Default route logic if not forced open by Ani
    if (!routeWantsToHide) {
      setCurrentCutoutVisibleProp(true);
    } else {
      hideCutoutTimerRef.current = setTimeout(() => {
        setCurrentCutoutVisibleProp(false);
      }, ROUTE_TRANSITION_GRACE_PERIOD_MS);
    }

    // Cleanup timer on component unmount or if dependencies change before timer fires
    return () => {
      if (hideCutoutTimerRef.current) {
        clearTimeout(hideCutoutTimerRef.current);
      }
    };
  }, [pathname, location.search, cutoutForceOpenedByAni]); // Added cutoutForceOpenedByAni
  // --- End of Cutout Visibility Logic ---

  const handleAniCutoutInteraction = useCallback(
    (action: "leave" | "enter_proximity") => {
      if (action === "leave") {
        setIsAniManuallyHidden(true);
        setCutoutForceOpenedByAni(false); // Ani left, no longer forcing open
      } else if (action === "enter_proximity") {
        // If Ani is entering proximity, it implies it wants the cutout open.
        setIsAniManuallyHidden(false); // Ani is no longer manually hiding it.
        setCutoutForceOpenedByAni(true); // Ani is actively trying to open/keep it open.
      }
    },
    [] // Dependencies are not needed as we are only setting states.
  );

  let showCutoutProp;
  if (aniHidden || isAniManuallyHidden) {
    showCutoutProp = false;
  } else if (cutoutForceOpenedByAni) {
    showCutoutProp = true;
  } else if (widgetViewState.isOpen && widgetViewState.mode === "docked" && isDesktop) {
    // Close cutout when ANI is open in docked mode
    showCutoutProp = false;
  } else {
    showCutoutProp = currentCutoutVisibleProp;
  }

  const handleAniDragStateChange = useCallback((isDragging: boolean) => {
    setIsAniBeingDragged(isDragging);
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(min-width: 1024px)");
    const onMediaChange = (event: MediaQueryListEvent) => {
      setIsDesktop(event.matches);
    };
    setIsDesktop(mediaQuery.matches);
    mediaQuery.addEventListener("change", onMediaChange);
    return () => mediaQuery.removeEventListener("change", onMediaChange);
  }, []);

  return (
    <div className="relative isolate flex w-full bg-white max-lg:flex-col lg:bg-slate-100 dark:bg-slate-900 h-full">
      {/* Glassmorphism Background */}
      <div className="absolute inset-0 z-0  bg-slate-700">
        {/* <Glassmorphism
          colors={themeColors}
          opacity={1}
          overlayOpacity={0.5}
          blur={8}
          className="w-full h-full"
        /> */}
        <GradientBlobContainer
          seed={currentTheme?.seed || "default background"}
          themeColors={currentTheme?.colors}
          dark
          position="bl"
          className="w-full h-full opacity-40"
          eager
          rasterize
        />
      </div>

      {/* Sidebar on desktop */}
      {pathname !== "/report-builder" && (
        <div className="relative z-20 max-lg:hidden h-full flex flex-col text-white font-sans">
          {sidebar}
        </div>
      )}

      {/* Sidebar on mobile */}
      <MobileSidebar
        open={showSidebar}
        close={() => setShowSidebar(false)}
        closeIcon={closeIcon}
      >
        <div className="text-white font-sans">
          {React.isValidElement(sidebar)
            ? React.cloneElement(
                sidebar as React.ReactElement<{ forceExpanded?: boolean }>,
                { forceExpanded: true }
              )
            : sidebar}
        </div>
      </MobileSidebar>

      {/* Navbar on mobile */}
      <header className="relative z-10 flex items-center px-4 lg:hidden">
        <div className="py-2.5">
          <NavbarItem
            onClick={() => setShowSidebar(true)}
            aria-label="Open navigation"
          >
            {openIcon}
          </NavbarItem>
        </div>
        <div className="min-w-0 flex-1">{navbar}</div>
      </header>

      {/* Content */}
      <main
        className={`relative z-10 flex flex-1 flex-col p-4 lg:min-w-0 max-h-dvh transition-[padding-right] duration-[350ms] ease-[cubic-bezier(0.32,0.72,0,1)] ${
          widgetViewState.isOpen && widgetViewState.mode === "docked" && isDesktop
            ? "lg:pr-[440px]"
            : ""
        }`}
      >
        {workspaceBanner ? (
          <div className="mb-4 shrink-0">{workspaceBanner}</div>
        ) : null}
        <div
          id="main-content"
          ref={mainContentRef}
          className={`flex-grow relative overflow-hidden lg:rounded-2xl  ${
            hasColumns ? "" : "overflow-y-auto"
          } `}
        >
          <CutoutMaskContainer
            ref={cutoutContainerRef}
            cutoutOffset={{ x: 0, y: 0 }}
            // Aiming for symmetrical morph by aligning initial radii of shrinking components
            outerCurveRadius={20} // Makes outer curves tighter
            innerCurveRadius={48} // Makes inner curve broader
            verticalConnectorLength={0} // Adjusts vertical straight part
            horizontalConnectorLength={0}
            simpleCornerRadius={17} // Radius of the simple rounded corner
            morphProgress={currentMorphProgress}
            animationDurationMs={MORPH_ANIMATION_DURATION}
            debug={false}
            showCutout={showCutoutProp}
          >
            {children}
          </CutoutMaskContainer>
        </div>
        {canUseAni ? (
          <AniChatWidget
            resourceId={resourceId ?? ""}
            context={chatContext}
            apiData={apiData}
            onApiAction={onApiAction}
            contentAreaRef={mainContentRef}
            onCutoutInteraction={handleAniCutoutInteraction}
            isCutoutCurrentlyOpen={showCutoutProp}
            onDragStateChange={handleAniDragStateChange}
          />
        ) : null}
      </main>
    </div>
  );
}
